import {
    addPendingAction,
    getPendingActions,
    removePendingAction,
    saveWorkOrders,
    saveExpenses,
    saveShifts,
    updateLastSyncTimestamp,
    clearOldData,
    getOfflinePhoto,
    removeOfflinePhoto,
    updateOfflinePhotoStatus,
} from './offlineDb';
import {
    setShiftSession,
    updateShiftSession,
    removeShiftSession as removeFirebaseShiftSession,
    checkWorkerSession,
    removeTaskSession,
    removeWorkerSession,
    getAllActiveTasks,
    getShiftSession,
} from './firebase';
import {
    submitClockOut,
    completeTask,
    addExpense,
    getWorkOrders,
    getExpenses,
    getShifts,
} from './api';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import {
    formatExpenseMessage,
    sendTelegramMessage,
    sendTelegramFile,
} from './telegram';
import { API_BASE } from './api';

// Add photo upload action type
export type PendingActionType =
    | 'clockIn'
    | 'finishJob'
    | 'clockOut'
    | 'startTask'
    | 'endTask'
    | 'addExpense'
    | 'uploadPhoto';

export async function syncPendingActions() {
    const pendingActions = await getPendingActions();
    let syncSuccess = true;
    let hasPhotoUploads = false;

    const { accessToken } = useAuthStore.getState();
    if (!accessToken) {
        console.log('No auth token available for sync');
        return false;
    }

    // Sort actions by timestamp and type to ensure proper order
    pendingActions.sort((a, b) => {
        if (a.timestamp !== b.timestamp) {
            return a.timestamp - b.timestamp;
        }
        // Ensure clockIn comes before finishJob and clockOut, and uploadPhoto comes last
        const typeOrder = {
            clockIn: 0,
            finishJob: 1,
            clockOut: 2,
            uploadPhoto: 3,
        };
        return (typeOrder[a.type] || 99) - (typeOrder[b.type] || 99);
    });

    // Early return if no actions to sync
    if (pendingActions.length === 0) {
        return true;
    }

    const { workerId } = useAuthStore.getState();
    if (!accessToken || !workerId) {
        console.log('No auth credentials available for sync');
        return false;
    }

    for (const action of pendingActions) {
        try {
            console.log('Processing action:', action.type, action.data);

            // Skip empty data check for photo uploads since they have different data structure
            if (!action.data && action.type !== 'uploadPhoto') {
                console.log('Invalid action data, removing:', action);
                await removePendingAction(action.id);
                continue;
            }

            // Validate action data
            // Skip validation for expense and photo upload actions
            if (
                action.type !== 'addExpense' &&
                action.type !== 'uploadPhoto' &&
                (!action.data.worker_id || !action.data.clockin_time)
            ) {
                console.log('Invalid action data, removing:', action);
                await removePendingAction(action.id);
                continue;
            }

            switch (action.type) {
                case 'uploadPhoto':
                    try {
                        hasPhotoUploads = true;
                        await handlePhotoUpload(action, accessToken);
                    } catch (error) {
                        console.error('Photo upload failed:', error);
                        syncSuccess = false;
                    }
                    break;

                case 'addExpense':
                    try {
                        const {
                            workerId,
                            amount,
                            comment,
                            date,
                            categoryId,
                            orderId,
                            action: expenseAction,
                            files,
                            categoryName,
                            woNumber,
                        } = action.data;
                        const { workerInfo } = useAuthStore.getState();

                        // Create enhanced worker info with expense details
                        const enhancedWorkerInfo = {
                            ...workerInfo,
                            categoryName,
                            woNumber,
                            files: files?.map((file) => ({
                                fileName: file.name,
                                fileSize: file.size,
                            })),
                        };
                        await addExpense(
                            accessToken,
                            workerId,
                            amount,
                            comment,
                            date,
                            categoryId,
                            orderId,
                            expenseAction,
                            files,
                        );

                        // Send Telegram notification after successful sync
                        if (workerInfo?.groupId) {
                            try {
                                // First send the expense message
                                await sendTelegramMessage(
                                    workerInfo.groupId,
                                    formatExpenseMessage(
                                        enhancedWorkerInfo,
                                        amount,
                                        comment,
                                    ),
                                );

                                // Send file attachments if any
                                if (files?.length > 0) {
                                    for (const file of files) {
                                        try {
                                            const caption =
                                                `📎 <b>Expense Attachment</b>\n\n` +
                                                `<b>File:</b> ${file.name}\n` +
                                                `<b>Size:</b> ${(file.size / 1024).toFixed(1)}KB\n` +
                                                `<b>Category:</b> ${categoryName || 'N/A'}\n` +
                                                `<b>Work Order:</b> ${woNumber ? `#${woNumber}` : 'N/A'}`;
                                            await sendTelegramFile(
                                                workerInfo.groupId,
                                                file,
                                                caption,
                                            );
                                        } catch (fileError) {
                                            console.error(
                                                'Failed to send file to Telegram:',
                                                fileError,
                                            );
                                            // Continue with other files even if one fails
                                        }
                                    }
                                }
                            } catch (error) {
                                console.error(
                                    'Failed to send Telegram notification:',
                                    error,
                                );
                                // Don't throw error here to allow expense to be saved even if notification fails
                            }
                        }

                        await removePendingAction(action.id);
                        toast.success('Expense synced successfully');
                    } catch (error) {
                        console.error('Failed to sync expense:', error);
                        throw error;
                    }
                    break;

                case 'clockIn':
                    try {
                        const existingSession = await getShiftSession(
                            action.data.worker_id,
                        );
                        if (existingSession) {
                            console.log(
                                'Session already exists, skipping clockIn',
                            );
                            await removePendingAction(action.id);
                            continue;
                        }
                        await setShiftSession(action.data);
                        await removePendingAction(action.id);
                    } catch (error) {
                        console.log('Failed to set shift session:', error);
                        if (
                            error.message.includes(
                                'already has an active shift',
                            )
                        ) {
                            await removePendingAction(action.id);
                        } else {
                            throw error;
                        }
                    }
                    break;

                case 'finishJob':
                    {
                        try {
                            const session = await getShiftSession(
                                action.data.worker_id,
                            );

                            // Check if session exists and is valid
                            if (!session) {
                                console.log(
                                    'No active session found, removing action',
                                );
                                await removePendingAction(action.id);
                                continue;
                            }

                            // Check if session is already finished
                            if (session.status === 'job_finished') {
                                console.log(
                                    'Session already finished, removing action',
                                );
                                await removePendingAction(action.id);
                                continue;
                            }

                            // Check if session is logged out
                            const workerSession = await checkWorkerSession(
                                action.data.worker_id,
                            );
                            if (!workerSession?.is_logged_in) {
                                console.log(
                                    'Worker session is logged out, removing action',
                                );
                                await removePendingAction(action.id);
                                continue;
                            }

                            await updateShiftSession(action.data.worker_id, {
                                finishjob_time: action.data.finishjob_time,
                                status: 'job_finished',
                            });
                            await removePendingAction(action.id);
                        } catch (error) {
                            if (
                                error instanceof Error &&
                                (error.message.includes(
                                    'No active shift session found',
                                ) ||
                                    error.message.includes(
                                        'No updates provided',
                                    ) ||
                                    error.message.includes(
                                        'Session has been logged out',
                                    ))
                            ) {
                                console.log(
                                    'Expected error, removing action:',
                                    error.message,
                                );
                                await removePendingAction(action.id);
                                continue;
                            }
                            throw error; // Re-throw other errors
                        }
                    }
                    break;

                case 'clockOut':
                    try {
                        try {
                            console.log('Submitting clock out to backend...');
                            await submitClockOut(accessToken, workerId, {
                                worker_id: parseInt(workerId),
                                clockin_time: Math.floor(
                                    action.data.clockin_time / 1000,
                                ),
                                finishjob_time: Math.floor(
                                    action.data.finishjob_time / 1000,
                                ),
                                clockout_time: Math.floor(
                                    action.data.clockout_time / 1000,
                                ),
                            });
                            console.log('Clock out submitted successfully');

                            // Clean up Firebase data after successful backend sync
                            console.log('Cleaning up Firebase data...');
                            await removeFirebaseShiftSession(
                                action.data.worker_id,
                            );

                            // Clean up any remaining task sessions
                            const taskSessions = await getAllActiveTasks(
                                action.data.worker_id,
                            );
                            for (const task of taskSessions) {
                                await removeTaskSession(
                                    action.data.worker_id,
                                    task.task_id,
                                );
                            }

                            // Finally, clean up the worker session
                            await removeWorkerSession(action.data.worker_id);

                            // Remove the pending action after successful sync and cleanup
                            await removePendingAction(action.id);
                            console.log(
                                'Clock out sync completed successfully',
                            );
                        } catch (error) {
                            console.error('Failed to submit clock out:', error);
                            throw error;
                        }
                    } catch (error) {
                        if (
                            error instanceof Error &&
                            error.message.includes(
                                'No active shift session found',
                            )
                        ) {
                            console.log(
                                'No active shift session, removing action',
                            );
                            await removePendingAction(action.id);
                            continue;
                        }
                        console.error('Clock out error:', error);
                        throw error;
                    }
                    break;

                case 'startTask':
                    // Handle start task sync
                    break;

                case 'endTask':
                    await completeTask(
                        action.data.token,
                        action.data.workerId,
                        action.data.taskId,
                        action.data.startTime,
                        action.data.endTime,
                        action.data.spentTime,
                    );
                    break;
            }

            await removePendingAction(action.id);
            console.log(`Successfully synced action: ${action.type}`);
        } catch (error) {
            console.error('Sync error details:', {
                actionType: action.type,
                error: error instanceof Error ? error.message : 'Unknown error',
                data: action.data,
            });
            syncSuccess = false;
            toast.error(`Failed to sync ${action.type}. Will retry later.`);
            break; // Stop syncing on first error
        }
    }

    return syncSuccess;
}

async function handlePhotoUpload(action: any, accessToken: string) {
    const { reportId, taskItem, file, metadata, photoId } = action.data;

    // First check if the photo still exists in offline storage
    const photo = await getOfflinePhoto(photoId);
    if (!photo) {
        console.log(
            'Photo no longer exists in offline storage, removing action',
        );
        await removePendingAction(action.id);
        return;
    }

    // Update status to uploading
    await updateOfflinePhotoStatus(photoId, 'uploading');

    const formData = new FormData();
    formData.append('photos', file);
    formData.append('taskItem', taskItem);
    formData.append('photoType', 'PROGRESS');
    // Add metadata as a JSON string
    // Add metadata as a separate field for each photo
    const metadataArray = [
        {
            latitude: metadata.latitude,
            longitude: metadata.longitude,
            accuracy: metadata.accuracy || 0,
            timestamp: metadata.timestamp,
            deviceInfo: metadata.deviceInfo,
        },
    ];
    formData.append('metadata', JSON.stringify(metadataArray));

    const response = await fetch(`${API_BASE}/bid-photos/${reportId}`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
    });

    if (!response.ok) {
        throw new Error('Failed to upload photo');
    }

    // Clean up after successful upload
    await removeOfflinePhoto(photoId);
    await removePendingAction(action.id);
}

async function syncOfflineData() {
    const { accessToken, workerId } = useAuthStore.getState();

    if (!accessToken || !workerId) {
        console.log('No auth credentials available for sync');
        return;
    }

    try {
        // Clear old data before syncing
        console.log('Clearing old data before sync...');
        try {
            await clearOldData();
        } catch (error) {
            console.error('Failed to clear old data:', error);
            // Continue with sync even if clear fails
        }

        // Fetch all data in parallel
        console.log('Fetching data from backend...');
        try {
            const [workOrdersResponse, expensesResponse, shiftsResponse] =
                await Promise.all([
                    getWorkOrders(accessToken, workerId),
                    getExpenses(accessToken),
                    getShifts(accessToken),
                ]);

            // Save all data to IndexedDB
            console.log('Saving fetched data to IndexedDB...');
            await saveWorkOrders(workOrdersResponse.orders || []);
            await saveExpenses(expensesResponse.expenses || []);
            await saveShifts(shiftsResponse || []);
        } catch (error) {
            console.error('Failed to fetch or save data:', error);
            throw error;
        }

        await updateLastSyncTimestamp();
        console.log('Data synchronized successfully');
        toast.success('Data synchronized successfully');
    } catch (error) {
        console.error('Failed to sync offline data:', error);
        toast.error('Failed to sync some data');
    }
}

// Custom event for data refresh
export const DATA_REFRESH_EVENT = 'dataRefresh';

let syncManagerInitialized = false;

export function initSyncManager() {
    if (syncManagerInitialized) {
        return;
    }

    let syncInProgress = false;

    window.addEventListener('online', async () => {
        if (syncInProgress) return;

        syncInProgress = true;
        toast.success('Back online! Syncing pending actions...');

        try {
            const syncSuccess = await syncPendingActions();
            if (syncSuccess) {
                await syncOfflineData();
                // Dispatch custom event to notify components to refresh their data
                window.dispatchEvent(new CustomEvent(DATA_REFRESH_EVENT));
                toast.success('Data refreshed successfully');
            }
        } finally {
            syncInProgress = false;
        }
    });

    // Optional: Add offline event listener
    window.addEventListener('offline', () => {
        toast.error(
            'Connection lost. Changes will be synced when back online.',
        );
    });

    syncManagerInitialized = true;
}

// Export types for TypeScript support
export type PendingActionType =
    | 'clockIn'
    | 'finishJob'
    | 'clockOut'
    | 'startTask'
    | 'endTask'
    | 'addExpense';

export interface PendingAction {
    id: string;
    type: PendingActionType;
    data: {
        worker_id: string;
        clockin_time: number;
        finishjob_time?: number;
        clockout_time?: number;
        status: 'working' | 'job_finished' | 'clocked_out';
        [key: string]: any;
    };
}
